import { Box, Link, Tooltip, Typography } from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import { useState } from "react";

import {
  OrganizationTemplateRubric,
  TemplateRubricFieldScore,
} from "features/Influencer/ContactList/schema";

const NUM_BARS = 5;
const VISIBLE_LIMIT = 2;

function getBarColor(pct: number): string {
  if (pct < 0.3) return red[400];
  if (pct < 0.8) return yellow[700];
  return green[500];
}

interface ScoreBarsProps {
  score: number;
  maxScore: number;
}

const ScoreBars = ({ score, maxScore }: ScoreBarsProps) => {
  const pct = score / maxScore;
  const filled = pct * NUM_BARS;
  const color = getBarColor(pct);

  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {Array.from({ length: NUM_BARS }).map((_, i) => {
        const fill = Math.min(1, Math.max(0, filled - i));
        return (
          <Box
            key={i}
            sx={{
              width: 28,
              height: 12,
              borderRadius: 0.5,
              bgcolor: fill === 0 ? "grey.200" : undefined,
              background:
                fill > 0 && fill < 1
                  ? `linear-gradient(to right, ${color} ${fill * 100}%, #e0e0e0 ${fill * 100}%)`
                  : undefined,
              backgroundColor: fill >= 1 ? color : undefined,
            }}
          />
        );
      })}
    </Box>
  );
};

interface RubricRowProps {
  field: TemplateRubricFieldScore;
}

const RubricRow = ({ field }: RubricRowProps) => {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {field.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: getBarColor(field.userScore / field.score),
          }}
        >
          {field.userScore}
          <Box component="span" sx={{ color: grey[500] }}>
            /{field.score}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ScoreBars score={field.userScore} maxScore={field.score} />
        {field.suggestions && (
          <Tooltip
            title={field.suggestions}
            placement="top"
            arrow
            enterTouchDelay={0}
          >
            <Typography
              variant="caption"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.65rem",
                cursor: "help",
                borderBottom: "1px dashed",
                borderColor: "primary.main",
                lineHeight: 1.2,
              }}
            >
              Suggestion
            </Typography>
          </Tooltip>
        )}
      </Box>

      {field.reasoning && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {field.reasoning}
        </Typography>
      )}
    </Box>
  );
};

interface Props {
  rubric: OrganizationTemplateRubric;
  showAll?: boolean;
}

const Rubrics = ({ rubric, showAll = false }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const hasMore = !showAll && rubric.fields.length > VISIBLE_LIMIT;
  const visible =
    showAll || expanded ? rubric.fields : rubric.fields.slice(0, VISIBLE_LIMIT);
  const nextItem =
    !showAll && !expanded && hasMore ? rubric.fields[VISIBLE_LIMIT] : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      {visible.map((field) => (
        <RubricRow key={field.name} field={field} />
      ))}
      {nextItem && (
        <Box
          sx={{
            filter: "blur(3px)",
            opacity: 0.5,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <RubricRow field={nextItem} />
        </Box>
      )}
      {hasMore && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setExpanded((prev) => !prev)}
          underline="hover"
          sx={{
            mb: 2,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "fit-content",
          }}
        >
          {expanded ? "View less ▲" : "View more ▼"}
        </Link>
      )}
    </Box>
  );
};

export default Rubrics;
